// Global "Ask Reggie" drawer for the Analytics section.
//
// Mounted once by NavigationWrapper but rendered through a portal onto
// document.body: the navigation wrapper is CSS-transformed, which would turn
// position: fixed into position: absolute and pin the drawer to the left.
// It slides in from the right over any /analytics page and stays mounted while
// the user is in the section, so closing it keeps the conversation (and lets an
// in-flight answer finish). It closes itself when they leave the section, and
// renders nothing unless the organization has isRhinoAnalystEnabled (CCR
// toggle) and the service URL is configured.
//
// Conversations are persisted per user+organization in localStorage.

import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import cx from 'classnames';

import RhinoAnalystPanel from './RhinoAnalystPanel';
import {
  closeAskReggie, conversationStorageKey, getAskReggieState, hydrateConversations,
} from '../reducers/rhinoanalystReducer';
import { getCurrentOrg } from '../selectors/organizationSelectors';
import { getLoggedInUser } from '../selectors/userSelectors';
import { isRhinoAnalystConfigured } from '../services/RhinoAnalystService';
import StorageService from '../services/StorageService';

export const isAskReggieAvailable = (organization) => !!organization?.isRhinoAnalystEnabled && isRhinoAnalystConfigured();

const PERSIST_DEBOUNCE_MS = 400;

const RhinoAnalystDrawer = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    isOpen, context, conversations, storageKey,
  } = useSelector(getAskReggieState);
  const currentOrganization = useSelector(getCurrentOrg);
  const currentUser = useSelector(getLoggedInUser);
  const persistTimer = useRef(null);

  const available = isAskReggieAvailable(currentOrganization);
  const inAnalytics = location.pathname.startsWith('/analytics');
  const nextKey = conversationStorageKey(currentUser?.id, currentOrganization?.id);

  // Load this user+org's saved conversations whenever the identity changes.
  useEffect(() => {
    if (!nextKey || nextKey === storageKey) return;
    let saved = {};
    try {
      saved = StorageService.readEntry(nextKey) || {};
    } catch (err) { /* storage unavailable */ }
    dispatch(hydrateConversations({ storageKey: nextKey, conversations: saved }));
  }, [nextKey, storageKey, dispatch]);

  // Persist (debounced; streaming updates arrive many times a second).
  useEffect(() => {
    if (!storageKey || storageKey !== nextKey) return undefined;
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      try {
        StorageService.createEntry(storageKey, conversations);
      } catch (err) { /* quota or private mode */ }
    }, PERSIST_DEBOUNCE_MS);
    return () => { if (persistTimer.current) clearTimeout(persistTimer.current); };
  }, [conversations, storageKey, nextKey]);

  useEffect(() => {
    if (isOpen && (!inAnalytics || !available)) dispatch(closeAskReggie());
  }, [isOpen, inAnalytics, available, dispatch]);

  if (!available || !inAnalytics || typeof document === 'undefined') return null;

  return ReactDOM.createPortal(
    <div className={cx('rhinoanalyst-drawer', { 'is-open': isOpen })} aria-hidden={!isOpen}>
      <RhinoAnalystPanel
        onClose={() => dispatch(closeAskReggie())}
        currentUser={currentUser}
        contextLabel={context.label}
        dateRange={context.dateRange}
        context={{ page: context.page, filters: context.filters }}
      />
    </div>,
    document.body,
  );
};

export default RhinoAnalystDrawer;
